import { demoFunc } from './dees-harness-tool-card.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessToolCall, THarnessContentBlock } from '../interfaces.js';
import {
  DeesHarnessToolRegistry,
  parseMcpName,
  type IHarnessToolDescriptor,
  type IHarnessToolRenderHelpers,
} from '../toolregistry.js';
import { builtinBodyRenderers } from './renderers.js';
import { harnessCardStyles, harnessPillStyles, harnessMonoStyles } from '../harness.styles.js';
import { harnessFormatDuration, harnessTruncate } from '../harness.helpers.js';
import '../dees-harness-content-blocks/dees-harness-content-blocks.js';
import '../../00group-dataview/dees-dataview-codebox/dees-dataview-codebox.js';
import '../../00group-feedback/dees-badge/dees-badge.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-tool-card': DeesHarnessToolCard;
  }
}

/**
 * Tool-call card: shared chrome (icon, label, subtitle, status pill,
 * duration, MCP badges) with a per-kind body. Bodies come from the built-in
 * renderer map or a descriptor's custom renderBody; kind resolution goes
 * through the tool registry.
 */
@customElement('dees-harness-tool-card')
export class DeesHarnessToolCard extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor call: IHarnessToolCall = { id: '', name: '', status: 'pending' };

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  @state()
  accessor expanded: boolean | undefined = undefined;

  @state()
  accessor outputExpanded: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    harnessCardStyles,
    harnessPillStyles,
    harnessMonoStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .header {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        width: 100%;
        padding: var(--dees-spacing-xs) var(--dees-spacing-md);
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-family: inherit;
        text-align: left;
      }

      .header:hover {
        background: var(--dees-color-hover);
      }

      .toolIcon {
        display: grid;
        place-items: center;
        width: 22px;
        flex: 0 0 22px;
        color: var(--dees-color-text-secondary);
      }

      .harness-card.status-running .toolIcon,
      .harness-card.status-pending .toolIcon {
        color: var(--dees-color-accent-primary);
      }

      .titleBlock {
        min-width: 0;
        flex: 1;
      }

      .titleRow {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
      }

      .toolLabel {
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        font-weight: 600;
        white-space: nowrap;
      }

      .badges {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
      }

      .subtitle {
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .metaColumn {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        flex: 0 0 auto;
      }

      .duration {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
        white-space: nowrap;
      }

      .body {
        display: grid;
        gap: var(--dees-spacing-sm);
        padding: 0 var(--dees-spacing-md) var(--dees-spacing-md);
      }

      .kvGrid {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: 2px var(--dees-spacing-md);
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .kvKey {
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
      }

      .kvValue {
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family-mono);
        overflow-wrap: anywhere;
      }

      .bodyImage {
        max-width: 100%;
        max-height: 420px;
        border-radius: var(--dees-radius-md);
        border: 1px solid var(--dees-color-border-subtle);
      }

      dees-dataview-codebox {
        font-family: var(--dees-font-family);
      }
    `,
  ];

  private get descriptor(): IHarnessToolDescriptor {
    return this.toolRegistry.resolve(this.enrichedCall);
  }

  /** call with mcp meta derived from the name when the adapter didn't set it */
  private get enrichedCall(): IHarnessToolCall {
    if (this.call.mcp) return this.call;
    const parsed = parseMcpName(this.call.name);
    return parsed ? { ...this.call, mcp: { serverName: parsed.serverName, toolName: parsed.toolName } } : this.call;
  }

  public get isOpen(): boolean {
    if (this.expanded !== undefined) return this.expanded;
    const descriptor = this.descriptor;
    if (this.call.status === 'error' || this.call.isError) return true;
    if (typeof descriptor.defaultExpanded === 'function') return descriptor.defaultExpanded(this.enrichedCall);
    return descriptor.defaultExpanded ?? false;
  }

  public render(): TemplateResult {
    const call = this.enrichedCall;
    const descriptor = this.descriptor;
    const label = typeof descriptor.label === 'function' ? descriptor.label(call) : descriptor.label;
    const subtitle = descriptor.subtitle?.(call) ?? '';
    const statusTone = call.isError ? 'error' : call.status === 'completed' ? 'success' : call.status;
    const duration = harnessFormatDuration(call.startedAt, call.finishedAt);
    return html`
      <div class="harness-card status-${call.isError ? 'error' : call.status} ${descriptor.destructive ? 'destructive' : ''}">
        <button
          class="header"
          type="button"
          aria-expanded=${this.isOpen ? 'true' : 'false'}
          @click=${() => {
            this.expanded = !this.isOpen;
          }}
        >
          <span class="toolIcon"><dees-icon .icon=${descriptor.icon} iconSize="15"></dees-icon></span>
          <span class="titleBlock">
            <span class="titleRow">
              <span class="toolLabel">${label}</span>
              ${this.renderMcpBadges(call)}
            </span>
            ${subtitle ? html`<span class="subtitle" title=${subtitle}>${subtitle}</span>` : ''}
          </span>
          <span class="metaColumn">
            ${duration ? html`<span class="duration">${duration}</span>` : ''}
            <span class="harness-pill tone-${statusTone}">
              ${call.isError ? 'error' : call.status}
            </span>
          </span>
        </button>
        ${this.isOpen ? html`<div class="body">${this.renderBody(call)}</div>` : ''}
      </div>
    `;
  }

  private renderMcpBadges(call: IHarnessToolCall): TemplateResult {
    if (!call.mcp) return html``;
    const annotations = call.mcp.annotations;
    return html`
      <span class="badges">
        <dees-badge type="default" .text=${call.mcp.serverName} rounded></dees-badge>
        ${annotations?.readOnlyHint ? html`<dees-badge type="success" text="read-only" rounded></dees-badge>` : ''}
        ${annotations?.destructiveHint ? html`<dees-badge type="error" text="destructive" rounded></dees-badge>` : ''}
        ${annotations?.openWorldHint ? html`<dees-badge type="warning" text="open-world" rounded></dees-badge>` : ''}
        ${annotations?.idempotentHint ? html`<dees-badge type="default" text="idempotent" rounded></dees-badge>` : ''}
      </span>
    `;
  }

  private renderBody(call: IHarnessToolCall): TemplateResult {
    const descriptor = this.descriptor;
    const helpers = this.createHelpers();
    if (descriptor.renderBody) return descriptor.renderBody(call, helpers);
    const builtin = builtinBodyRenderers[descriptor.kind] ?? builtinBodyRenderers.unknown;
    return builtin(call, helpers);
  }

  private createHelpers(): IHarnessToolRenderHelpers {
    return {
      codeBlock: (code, language, filename) => html`
        <dees-dataview-codebox
          .progLang=${language ?? 'plaintext'}
          .filename=${filename ?? ''}
          .codeToDisplay=${code}
        ></dees-dataview-codebox>
      `,
      diffBlock: (before, after, options) => html`
        <dees-dataview-codebox
          .progLang=${options?.language ?? 'plaintext'}
          .filename=${options?.filename ?? ''}
          .diffView=${options?.view ?? 'inline'}
          .codeBefore=${before}
          .codeToDisplay=${after}
        ></dees-dataview-codebox>
      `,
      monoBlock: (text, options) => {
        const maxChars = options?.maxChars ?? 6000;
        const { text: shown, truncated } = harnessTruncate(text, this.outputExpanded ? Number.MAX_SAFE_INTEGER : maxChars);
        return html`
          <pre class="harness-mono ${options?.tone === 'error' ? 'tone-error' : ''}">${shown}${truncated ? '…' : ''}</pre>
          ${truncated
            ? html`
                <button
                  class="harness-mono-expand"
                  type="button"
                  @click=${() => {
                    this.outputExpanded = true;
                  }}
                >
                  Show all
                </button>
              `
            : ''}
        `;
      },
      keyValues: (pairs) => html`
        <div class="kvGrid">
          ${pairs.map(([key, value]) => html`
            <div class="kvKey">${key}</div>
            <div class="kvValue">${value}</div>
          `)}
        </div>
      `,
      contentBlocks: (blocks: THarnessContentBlock[]) => html`
        <dees-harness-content-blocks .blocks=${blocks}></dees-harness-content-blocks>
      `,
      image: (dataBase64, mimeType, alt) => html`
        <img class="bodyImage" src=${`data:${mimeType};base64,${dataBase64}`} alt=${alt ?? 'tool output image'} />
      `,
    };
  }
}
